import {
  defaultVariantColorsResolver,
  parseThemeColor,
  rem,
  type VariantColorResolverResult,
  type VariantColorsResolver,
  type VariantColorsResolverInput,
} from '@mantine/core';
import { contrastRatio, inkDarkening, minimumTextContrast } from './contrast';
import { navyColours, secondaryColour } from './tokens';

/**
 * The CSS variable that switches the light-scheme ink correction on (`1`, light scheme) and off
 * (`0`, dark and blue). Defined per scheme by `cssVariablesResolver` in `theme.ts`.
 */
export const tintInkScaleVariable = '--charlie-tint-ink-scale';

const HEX = /^#[0-9a-f]{6}$/i;
const NAVY_INK = navyColours[7];
const cache = new Map<string, unknown>();

function memo<T>(key: string, compute: () => T): T {
  if (cache.has(key)) return cache.get(key) as T;
  const value = compute();
  cache.set(key, value);
  return value;
}

/** `color` mixed with black by `percent`, scaled to nothing outside the light scheme. */
function lightSchemeDarkened(color: string, percent: number): string {
  return `color-mix(in srgb, ${color}, black calc(var(${tintInkScaleVariable}) * ${String(percent)}%))`;
}

type ThemeColour = { name: string; scale: readonly string[]; shade: number | undefined };

function themeColour(input: VariantColorsResolverInput): ThemeColour | undefined {
  const parsed = parseThemeColor({
    color: input.color || input.theme.primaryColor,
    theme: input.theme,
  });
  const scale = parsed.isThemeColor ? input.theme.colors[parsed.color] : undefined;
  if (!scale?.every((hex) => HEX.test(hex))) return undefined;
  return { name: parsed.color, scale, shade: parsed.shade };
}

function primaryShade(input: VariantColorsResolverInput): number {
  const shade = input.theme.primaryShade;
  return typeof shade === 'number' ? shade : shade.light;
}

/** @desc Mantine's light-variant grounds in the light scheme: the 10% tint at rest, 12% on hover. */
export const LIGHT_TINT_ALPHA = 0.1;
export const LIGHT_HOVER_TINT_ALPHA = 0.12;

/**
 * Light variant: Mantine draws the text in the hue's own primary shade on a 10% tint of it (12% on
 * hover), which is under AA for the saturated hues (yellow worst, then orange, green, red, and the
 * brand accent at 4.3:1). Mix in exactly as much black as the hue needs on the DARKER of the two
 * grounds — the ink does not change on hover, so it has to clear both (snag S24: sized for the rest
 * tint alone, a light Button fell to 4.39:1 under the pointer). Light scheme only — dark and blue
 * draw a pale shade on a dark ground that already clears 8:1.
 */
function light(input: VariantColorsResolverInput, resolved: VariantColorResolverResult) {
  const colour = themeColour(input);
  if (!colour) return resolved;
  const { scale, shade } = colour;
  // Mirror defaultVariantColorsResolver: tint and ink are the primary shade, or with an explicit
  // shade the tint is that shade and the ink is capped at 6.
  const tint = scale[shade ?? primaryShade(input)];
  const ink = scale[shade === undefined ? primaryShade(input) : Math.min(shade, 6)];
  if (!tint || !ink) return resolved;
  const percent = memo(`light|${ink}|${tint}`, () =>
    Math.max(
      inkDarkening(ink, tint, LIGHT_TINT_ALPHA),
      inkDarkening(ink, tint, LIGHT_HOVER_TINT_ALPHA),
    ),
  );
  if (percent === 0) return resolved;
  return { ...resolved, color: lightSchemeDarkened(resolved.color, percent) };
}

/**
 * Outline variant: text and border in the hue's primary shade on the page, under AA for the same
 * hues. Darkened against white, light scheme only, the same way as the light variant.
 */
function outline(input: VariantColorsResolverInput, resolved: VariantColorResolverResult) {
  const colour = themeColour(input);
  if (!colour) return resolved;
  const ink = colour.scale[colour.shade ?? primaryShade(input)];
  if (!ink) return resolved;
  const percent = memo(`outline|${ink}`, () => inkDarkening(ink, ink, 0));
  if (percent === 0) return resolved;
  const color = lightSchemeDarkened(resolved.color, percent);
  return { ...resolved, color, border: `${rem(1)} solid ${color}` };
}

type FilledChoice = { shade: number; ink: 'white' | 'navy' };

/**
 * The filled treatment that reads at AA, keeping to the hue: white on the drawn shade or the
 * first darker one that clears 4.5:1; failing that (yellow, orange, green, lime), the style
 * guide's navy ink on the drawn shade or the first lighter one that clears it.
 */
function filledChoice(scale: readonly string[], drawn: number): FilledChoice | undefined {
  for (let shade = drawn; shade < scale.length; shade += 1) {
    const fill = scale[shade];
    if (fill && contrastRatio(secondaryColour, fill) >= minimumTextContrast)
      return { shade, ink: 'white' };
  }
  for (let shade = drawn; shade >= 0; shade -= 1) {
    const fill = scale[shade];
    if (fill && contrastRatio(NAVY_INK, fill) >= minimumTextContrast) return { shade, ink: 'navy' };
  }
  return undefined;
}

/**
 * Filled variant: Mantine puts white text on the primary shade, which fails for red (4.2:1) and
 * far worse for yellow, orange, green, teal, cyan and lime — in every scheme, since the fill does
 * not change with it. Pick the nearest fill and ink within the palette that pass.
 */
function filled(input: VariantColorsResolverInput, resolved: VariantColorResolverResult) {
  if (input.autoContrast) return resolved;
  const colour = themeColour(input);
  if (!colour) return resolved;
  const { name, scale } = colour;
  const drawn = colour.shade ?? primaryShade(input);
  const choice = memo(`filled|${name}|${String(drawn)}`, () => filledChoice(scale, drawn));
  if (!choice || (choice.shade === drawn && choice.ink === 'white')) return resolved;

  const ink = choice.ink === 'white' ? 'var(--mantine-color-white)' : 'var(--mantine-color-navy-7)';
  // Hover steps away from the ink: darker under white text, lighter under navy
  const hoverShade =
    choice.ink === 'white'
      ? Math.min(choice.shade + 1, scale.length - 1)
      : Math.max(choice.shade - 1, 0);
  return {
    ...resolved,
    background: `var(--mantine-color-${name}-${String(choice.shade)})`,
    hover: `var(--mantine-color-${name}-${String(hoverShade)})`,
    color: ink,
    hoverColor: ink,
  };
}

/**
 * Mantine's colours, corrected so text on a tinted, outlined or filled ground clears 4.5:1 —
 * computed per hue from the palette rather than hand-picked, so a hue that already passes (navy,
 * grey, the accent's filled stop) is left untouched.
 *
 * This replaces the hand-wired `tintedText` class the portal applied badge by badge, so every
 * Badge, Alert, ThemeIcon, Chip and Button gets it — including those rendered inside insights
 * fragments, which never saw the portal's class.
 */
export const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  switch (input.variant) {
    case 'light':
      return light(input, resolved);
    case 'outline':
      return outline(input, resolved);
    case 'filled':
      return filled(input, resolved);
    default:
      return resolved;
  }
};
